import React, { ElementType, forwardRef, PropsWithChildren, ReactElement, Ref } from 'react'

import { bemJoin } from 'bem-join'
import classnames from 'classnames'

import { PolymorphicComponentProps } from '@app/lib/types'

import './Button.scss'

export type ButtonProps<T extends ElementType> = PolymorphicComponentProps<
  T,
  {
    color?: 'primary' | 'secondary' | 'success' | 'danger' | 'neutral'
    variant?: 'outlined' | 'solid' | 'ghost'
    size?: 'min' | 'xs' | 'sm' | 'md' | 'lg'
    loading?: boolean
    leftIcon?: ReactElement
    rightIcon?: ReactElement
  }
>

const b = bemJoin('lawyer-button')

export const Button = forwardRef(
  <T extends ElementType>(
    {
      as,
      children,
      className,
      color = 'neutral',
      variant = 'ghost',
      size = 'md',
      leftIcon,
      rightIcon,
      loading,
      ...props
    }: PropsWithChildren<ButtonProps<T>>,
    ref: Ref<any>
  ) => {
    const Component = as || 'button'
    return (
      <Component
        ref={ref}
        className={classnames(
          b({
            ...(color && { [color]: true }),
            ...(variant && { [variant]: true }),
            ...(size && { [size]: true }),
            disabled: props.disabled,
            loading
          }),
          className
        )}
        {...props}
      >
        {leftIcon && <div className={b('left-icon')}>{leftIcon}</div>}
        <div className={b('text')}>{children}</div>
        {rightIcon && <div className={b('right-icon')}>{rightIcon}</div>}
      </Component>
    )
  }
)
